import { useCallback } from 'react'

import { fetchWidgetToken } from '../services/auth-api'
import type { WidgetScope } from '../types'

// Refresh the token this many ms before its JWT `exp` claim.
const REFRESH_LEEWAY_MS = 60 * 1000

interface CacheEntry {
  token: string
  expiresAt: number
  inflight: Promise<string> | null
}

// Module-level cache shared across all useWidgetToken consumers. Keyed by
// the sorted scopes string so widgets requesting the same scopes (e.g. the
// three profile-family widgets on OnboardingProfile) share one fetch and
// the same cached token across the SPA session. Cleared on hard reload.
const widgetTokenCache = new Map<string, CacheEntry>()

function getCacheKey(scopes: WidgetScope[]): string {
  return [...scopes].sort().join(',')
}

function getTokenExpiryMs(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 : 0
  } catch {
    return 0
  }
}

async function loadWidgetToken(scopes: WidgetScope[]): Promise<string> {
  const cacheKey = getCacheKey(scopes)
  const entry = widgetTokenCache.get(cacheKey)

  if (entry?.token && Date.now() < entry.expiresAt - REFRESH_LEEWAY_MS) {
    return entry.token
  }

  if (entry?.inflight) {
    return entry.inflight
  }

  const request = fetchWidgetToken(scopes)
    .then((response) => {
      widgetTokenCache.set(cacheKey, {
        token: response.token,
        expiresAt: getTokenExpiryMs(response.token),
        inflight: null,
      })
      return response.token
    })
    .catch((error) => {
      // Drop the failed inflight so the next call retries instead of
      // forever returning the rejected promise.
      const current = widgetTokenCache.get(cacheKey)
      if (current?.inflight === request) {
        widgetTokenCache.delete(cacheKey)
      }
      throw error
    })

  widgetTokenCache.set(cacheKey, {
    token: entry?.token ?? '',
    expiresAt: entry?.expiresAt ?? 0,
    inflight: request,
  })

  return request
}

/**
 * Returns a stable async function suitable for the WorkOS widget `authToken`
 * prop. The function fetches a widget token from the backend, caches it in
 * a module-level Map (shared across all hook consumers in the SPA session),
 * refreshes it ~60s before expiry, and de-duplicates concurrent calls.
 *
 * Pass `[]` for widgets that need a widget token but no specific scopes
 * (UserProfile, UserSecurity, UserSessions per WorkOS docs).
 */
export function useWidgetToken(scopes: WidgetScope[]): () => Promise<string> {
  // Stable identity across renders so the widget doesn't tear down.
  const scopesKey = getCacheKey(scopes)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(() => loadWidgetToken(scopes), [scopesKey])
}
